import React, { ElementType, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Button,
  Icon,
  Tag,
  useDisclosure,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Spinner,
} from "@chakra-ui/react";
import {
  FaCrown,
  FaClock,
  FaDollarSign,
  FaRedo,
  FaCheckCircle,
  FaEdit,
  FaHistory,
  FaTimesCircle,
  FaMoneyBillWave,
  FaMoneyBill,
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import { getOrganizationPaymentHistory, getOrganizationSubscription, updateOrganizationSubscription } from "../hooks/subscription";
import { OrganizationPaymentRecord, OrganizationSubscription } from "../utils/types";



const ManageSubscriptionPage: React.FC = () => {
  const { orgId } = useParams();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [subscription, setSubscription] = useState<OrganizationSubscription | null>(null);
  const [payments, setPayments] = useState<OrganizationPaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<OrganizationSubscription>>({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const subRes = await getOrganizationSubscription(orgId || "");
      const payRes = await getOrganizationPaymentHistory(orgId || "");
        console.log(subRes.data, payRes.data);
      if (subRes.success && subRes.data) setSubscription(subRes.data[0]);
      if (payRes.success && payRes.data) setPayments(payRes.data[0]);
    } catch (err) {
      toast({
        title: "Failed to load subscription data",
        status: "error",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [orgId]);

  const handleEdit = () => {
    if (!subscription) return;
    setForm({
      tier: subscription.tier,
      billing_cycle_days: subscription.billing_cycle_days,
      auto_renew: subscription.auto_renew,
    });
    onOpen();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateOrganizationSubscription(orgId || "", form);
      if (res.success) {
        toast({ title: "Subscription updated", status: "success" });
        fetchData();
        onClose();
      } else {
        toast({ title: "Update failed", status: "error" });
      }
    } catch (err) {
      toast({ title: "Error saving changes", status: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <Flex justify="center" align="center" h="80vh">
        <Spinner size="xl" color="#006400" />
      </Flex>
    );

  return (
    <Box p={6} bg="gray.50" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={3}>
          <Icon as={FaMoneyBill as ElementType} boxSize={7} color="#006400" />
          <Heading size="lg">Manage Subscription</Heading>
        </HStack>
        <Button
          colorScheme="green"
          size={'sm'}
          bg={'#006400'}
          leftIcon={<Icon as={FaEdit as ElementType} boxSize={5} />}
          onClick={handleEdit}
          variant="solid"
        >
          Edit Subscription
        </Button>
      </Flex>

      {/* Subscription Overview */}
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
        <Card shadow="sm" borderRadius="2xl">
          <CardHeader borderBottomWidth="1px">
            <HStack>
              <Icon as={FaMoneyBillWave as ElementType} color="#006400" />
              <Heading size="md">Subscription Overview</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            {subscription ? (
              <VStack align="start" spacing={3}>
                <HStack>
                  <Text fontWeight="medium">Plan:</Text>
                  <Tag colorScheme="green">{subscription.tier_display}</Tag>
                </HStack>

                <HStack>
                  <Icon as={FaClock as ElementType} color="#006400" />
                  <Text fontWeight="medium">Joined:</Text>
                  <Text>{new Date(subscription.date_joined).toLocaleDateString()}</Text>
                </HStack>

                {subscription.is_trial && (
                  <Tag colorScheme="orange">Trial - {subscription.trial_days} days</Tag>
                )}

                <HStack>
                  <Text fontWeight="medium">Billing Cycle:</Text>
                  <Text>{subscription.billing_cycle_days} days</Text>
                </HStack>

                <HStack>
                  <Text fontWeight="medium">Next Billing Date:</Text>
                  <Text>
                    {subscription.next_billing_date
                      ? new Date(subscription.next_billing_date).toLocaleDateString()
                      : "N/A"}
                  </Text>
                </HStack>

                <HStack>
                  <Text fontWeight="medium">Auto Renew:</Text>
                  {!subscription.auto_renew ? (
                    <HStack>
                      <Icon as={FaCheckCircle as ElementType} color="green.500" />
                      <Text>Enabled</Text>
                    </HStack>
                  ) : (
                    <HStack>
                      <Icon as={FaTimesCircle as ElementType} color="red.500" />
                      <Text>Disabled</Text>
                    </HStack>
                  )}
                </HStack>
              </VStack>
            ) : (
              <Text>No subscription found.</Text>
            )}
          </CardBody>
        </Card>

        {/* Payment History */}
        <Card shadow="sm" borderRadius="2xl">
          <CardHeader borderBottomWidth="1px">
            <HStack>
              <Icon as={FaHistory as ElementType} color="#006400" />
              <Heading size="md">Payment History</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Amount</Th>
                    <Th>Status</Th>
                    <Th>Reference</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {payments.length > 0 ? (
                    payments.map((p) => (
                      <Tr key={p.id}>
                        <Td>{new Date(p.date).toLocaleDateString()}</Td>
                        <Td>
                          <HStack>
                            <Icon as={FaDollarSign as ElementType} color="#006400" />
                            <Text>${p.amount}</Text>
                          </HStack>
                        </Td>
                        <Td>
                          <Tag
                            colorScheme={
                              p.status === "SUCCESS"
                                ? "green"
                                : p.status === "FAILED"
                                ? "red"
                                : "orange"
                            }
                          >
                            {p.status}
                          </Tag>
                        </Td>
                        <Td>{p.reference}</Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={4}>
                        <Text color="gray.500" textAlign="center">
                          No payments found
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      </Grid>

      {/* Edit Subscription Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader>Edit Subscription</ModalHeader>
          <Divider />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Plan Tier</FormLabel>
                <Select
                  value={form.tier}
                  onChange={(e) => setForm({ ...form, tier: e.target.value })}
                >
                  <option value="BASIC">Basic</option>
                  <option value="PRO">Pro</option>
                  <option value="ENTERPRISE">Enterprise</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Billing Cycle (Days)</FormLabel>
                <Input
                  type="number"
                  value={form.billing_cycle_days || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      billing_cycle_days: Number(e.target.value),
                    })
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Auto Renew</FormLabel>
                <Select
                  value={form.auto_renew ? "true" : "false"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      auto_renew: e.target.value === "true",
                    })
                  }
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleSave}
              isLoading={saving}
              leftIcon={<Icon as={FaCheckCircle as ElementType} boxSize={5} />}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ManageSubscriptionPage;
